import styled from 'styled-components'
import { t } from 'ttag'
import { useCallback, useEffect } from 'react'

import { features } from '@h2/components/media'
import { PG } from '@h2/components/colors'
import { Heading } from '@h2/components/typography/heading'
import { Text } from '@h2/components/typography/text'
import { useForm } from '@h2/common/components/hooks/useForm'
import { AdultEducationIcon } from '@h2/components/icons/adultEducation'
import { HigherEducationIcon } from '@h2/components/icons/higherEducation'
import { K12Icon } from '@h2/components/icons/k12'
import { Card } from '@h2/common/components/card'

import { props } from './props'

import { Explorer } from '../highlights/explorer'
import { Back } from '../back'
import { Submit } from '../submit'
import { Controls } from '../controls'
import { RadioButton, RadioButtonGroup } from '../radioButton'

const Form = styled.form`
  display: flex;
  flex-direction: column;
  height: 100%;

  > h2 {
    font-size: 24px;
    line-height: 28px;
  }

  > fieldset {
    flex-direction: column;
  }

  > div:nth-last-child(2) {
    margin: 30px 0 0 0;
  }

  @media (${features.minWidth.laptop}) {
    > h2 {
      font-size: 30px;
      line-height: 34px;
    }

    > fieldset {
      flex-direction: row;
    }

    > div:nth-last-child(2) {
      margin: auto 0;
    }
  }
`
const Student = ({
  inDialog,
  properties,
  validate,
  isValidating,
  formErrors,
  renderPanel
}) => {
  const { register, handleOnSubmit, setValue, setValidation, isInvalid } = useForm({})
  const onSubmit = useCallback(form => validate(form), [validate])

  useEffect(() => {
    if (formErrors) {
      setValidation(formErrors)
    }
  }, [formErrors, setValidation])

  useEffect(() => {
    if (properties.studentType) {
      setValue({
        studentType: properties.studentType
      })
    }
  }, [setValue, properties.studentType])

  return (
    <Card inDialog={inDialog}>
      <Card.Highlight color={PG}>
        <Explorer type="student" />
      </Card.Highlight>
      <Card.Content>
        <Form id="reg-student" onSubmit={handleOnSubmit(onSubmit)}>
          <Heading level="2" align="center" mb="12px">
            {t`What are you attending?`}
          </Heading>
          <Text align="center">{t`Select the option that most closely describes where you currently are:`}</Text>
          <RadioButtonGroup {...register('studentType', { required: true })}>
            <RadioButton value="k12">
              <K12Icon size="large" />
              <span className="flex">
                <span>{t`K-12`}</span>
                <span>{t`I'm in kindergarten through 12th grade.`}</span>
              </span>
            </RadioButton>
            <RadioButton value="higher_ed">
              <HigherEducationIcon size="large" />
              <span className="flex">
                <span>{t`Post Secondary`}</span>
                <span>{t`I'm in college or graduate school.`}</span>
              </span>
            </RadioButton>
            <RadioButton value="adult">
              <AdultEducationIcon size="large" />
              <span className="flex">
                <span>{t`Adult Ed`}</span>
                <span>{t`I'm in a certification or adult education program.`}</span>
              </span>
            </RadioButton>
          </RadioButtonGroup>
          <Controls>
            <Back onClick={() => renderPanel('explorerType')} />
            <Submit
              text={t`Next`}
              isLoading={isValidating}
              isDisabled={isInvalid || isValidating}
            />
          </Controls>
        </Form>
      </Card.Content>
      <Card.Detour>
        <Back onClick={() => renderPanel('explorerType')} />
        <Submit
          text={t`Next`}
          form="reg-student"
          isLoading={isValidating}
          isDisabled={isInvalid || isValidating}
        />
      </Card.Detour>
    </Card>
  )
}

Student.propTypes = { ...props }

export { Student }
